'use client';

import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/navigation';
import { OPEN_COOKIE_BANNER_EVENT } from '@/components/layout/CookieBanner';
import BrandWordmark from '@/components/ui/BrandWordmark';
import { TikTokIcon, InstagramIcon } from '@/components/ui/SocialIcons';

const CONTACTS = [
  { name: 'Yurii', phone: '+48 734 670 850' },
  { name: 'Sergii', phone: '+48 888 495 604' },
] as const;

export default function Footer() {
  const t = useTranslations('footer');
  const tBrand = useTranslations('brand');
  const pathname = usePathname();

  // Shared keyboard-focus ring for footer interactives (gold, offset on black).
  const focusRing =
    'rounded-sm focus-visible:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#000]';

  // Brand → home/hero. On the homepage smooth-scroll to the top; on a sub-page
  // (privacy, cookies) let the link navigate home. Mirrors the Header brand.
  function goHome(e: React.MouseEvent) {
    if (pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  return (
    <footer className="relative border-t border-outline-variant/20 bg-black px-margin-mobile pt-section-gap pb-[calc(6rem+env(safe-area-inset-bottom))] lg:px-margin-desktop lg:pb-stack-lg">
      {/* Extra bottom padding below lg so the copyright line clears the fixed
          bottom dock; restored to pb-stack-lg at lg, where the dock pins to top. */}
      {/* Brand logo — flush to the far-left gutter. On 2xl (where the centered
          columns leave a wide left margin) it's absolute so it sits beside the
          columns without shifting them; below 2xl it stacks above. The mark is a
          placeholder SVG at public/assets/brand/logo.svg — overwrite it for real. */}
      <div className="mb-10 2xl:absolute 2xl:left-margin-desktop 2xl:top-section-gap 2xl:mb-0">
        <Link
          href="/"
          onClick={goHome}
          aria-label={tBrand('name')}
          className="group inline-flex shrink-0 items-center gap-3 text-primary transition-colors focus-visible:outline-none"
        >
          <BrandWordmark className="h-10 w-auto shrink-0 brand-glow" />
        </Link>
      </div>

      {/* Columns nudged right on desktop so the middle (Quick Contact) column lands
          centered under the form's centred Next button. Equal grid + gaps preserve
          the col-1/col-3 spacing; the shift just repositions the whole block. */}
      <div className="mx-auto w-full max-w-6xl lg:translate-x-24">
        <div className="w-full grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-16">
          {/* Company Information */}
          <div className="space-y-stack-md">
            <h2 className="text-label-caps font-bold uppercase tracking-[0.2em] text-on-surface">
              {t('col1Title')}
            </h2>
            <div className="space-y-stack-sm">
              <p className="text-body-md font-semibold text-on-surface">{t('companyLegal')}</p>
              <p className="text-body-md text-on-surface-variant">{t('seat')}</p>
            </div>
            <Link
              href="/blog"
              className={`inline-block py-1 text-body-md text-on-surface-variant transition-colors hover:text-primary ${focusRing}`}
            >
              {t('blog')}
            </Link>
            <div className="-ml-3 flex flex-wrap items-center pt-stack-sm">
              <a
                href="https://www.tiktok.com/@Yurii_Yat"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className={`inline-flex h-11 w-11 items-center justify-center text-on-surface-variant transition-colors hover:text-primary ${focusRing}`}
              >
                <TikTokIcon className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/yat_fliz__wykonczenie_warszawa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className={`inline-flex h-11 w-11 items-center justify-center text-on-surface-variant transition-colors hover:text-primary ${focusRing}`}
              >
                <InstagramIcon className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Contact */}
          <div className="space-y-stack-md">
            <h2 className="text-label-caps font-bold uppercase tracking-[0.2em] text-on-surface">
              {t('col2Title')}
            </h2>
            {CONTACTS.map((contact) => (
              <div key={contact.name} className="space-y-1">
                <p className="text-body-md font-semibold text-on-surface">{contact.name}</p>
                <a
                  href={`tel:${contact.phone.replace(/\s/g, '')}`}
                  className={`inline-block py-1 text-body-md font-medium text-on-surface-variant transition-colors hover:text-primary ${focusRing}`}
                >
                  {contact.phone}
                </a>
                <p className="whitespace-nowrap text-body-sm text-on-surface-variant">{t('contactMethods')}</p>
              </div>
            ))}
            <div className="pt-stack-md">
              <p className="text-body-md text-on-surface-variant">{t('hours')}</p>
            </div>
          </div>

          {/* Legal */}
          <div className="space-y-stack-md">
            <h2 className="text-label-caps font-bold uppercase tracking-[0.2em] text-on-surface">
              {t('col3Title')}
            </h2>
            <div className="flex flex-col gap-stack-sm">
              <Link
                href="/privacy"
                className={`inline-block py-1 text-body-md text-on-surface-variant transition-colors hover:text-primary ${focusRing}`}
              >
                {t('privacy')}
              </Link>
              <Link
                href="/cookies"
                className={`inline-block py-1 text-body-md text-on-surface-variant transition-colors hover:text-primary ${focusRing}`}
              >
                {t('cookies')}
              </Link>
              {/* Re-opens the cookie consent banner so visitors can change a
                  previously saved choice (required for RODO/GDPR withdrawal). */}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_BANNER_EVENT))}
                className={`inline-block cursor-pointer py-1 text-left text-body-md text-on-surface-variant transition-colors hover:text-primary ${focusRing}`}
              >
                {t('manageCookies')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-stack-lg w-full max-w-6xl border-t border-outline-variant/20 pt-stack-lg text-center">
        <p className="text-label-caps uppercase tracking-widest text-outline">{t('copyright', { year: String(new Date().getFullYear()) })}</p>
      </div>
    </footer>
  );
}
